import React from 'react';
import { Button, Tooltip, makeStyles, shorthands } from '@fluentui/react-components';
import { Checkmark16Regular, Copy16Regular } from '@fluentui/react-icons';

const useStyles = makeStyles({
    checkMark: {
        color: 'green',
        fontWeight: 'bold',
    },
    button: {
        minWidth: 'unset',
        ...shorthands.border('none'),
        backgroundColor: 'transparent',
        color: 'rgba(0,0,0,0.7)',
        ...shorthands.borderRadius('5px'),
        paddingTop: '1px',
        paddingBottom: '1px',
        paddingLeft: '3px',
        paddingRight: '3px',
        ':hover': {
            backgroundColor: '#f2f2f2',
            color: 'rgba(0,0,0,0.85)',
            transition: 'background-color 0.3s ease-in-out',
        },
    },
});

interface CopyChatButtonProps {
    message: string;
}

const formatTable = (tableString: string): string => {
    const rows = tableString.split('\n');
    const pre: string[] = [];
    const table: string[] = [];
    const post: string[] = [];

    let tableStarted = false;

    for (const row of rows) {
        if (!row.includes('|')) {
            if (!tableStarted) {
                pre.push(row);
            } else {
                post.push(row);
            }
            continue;
        }

        tableStarted = true;
        table.push(row);
    }

    if (table.length >= 2) {
        table.splice(1, 1);
    }

    const formattedTable = table.map((row) => row.replace(/^\s*\|\s*|\s*\|\s*$/g, '').replace(/\s*\|\s*/g, '\t'));

    return [...pre, ...formattedTable, ...post].join('\n');
};

const CopyChatButton: React.FC<CopyChatButtonProps> = ({ message }) => {
    const styles = useStyles();
    const [copySuccess, setCopySuccess] = React.useState(false);

    const handleCopyToClipboard = React.useCallback(async (): Promise<void> => {
        const transformedPlainTextString = message.replace(/\*\*(.*?)\*\*/g, '$1');
        const transformedHtmlString = message.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br>');

        try {
            if (message.includes('|') && 'ClipboardItem' in window) {
                const formattedTable = formatTable(transformedPlainTextString);
                await navigator.clipboard.write([
                    new window.ClipboardItem({
                        'text/plain': new Blob([formattedTable], {
                            type: 'text/plain',
                        }),
                    }),
                ]);
            } else if ('ClipboardItem' in window) {
                await navigator.clipboard.write([
                    new window.ClipboardItem({
                        'text/plain': new Blob([transformedPlainTextString], {
                            type: 'text/plain',
                        }),
                        'text/html': new Blob([transformedHtmlString], {
                            type: 'text/html',
                        }),
                    }),
                ]);
            } else {
                await navigator.clipboard.writeText(transformedPlainTextString);
            }

            setCopySuccess(true);
            window.setTimeout(() => {
                setCopySuccess(false);
            }, 2000);
        } catch (error) {
            console.error('Failed to copy chat message.', error);
            setCopySuccess(false);
        }
    }, [message]);

    return (
        <Tooltip content={copySuccess ? 'Copied' : 'Copy message'} relationship="label">
            <Button
                className={styles.button}
                icon={copySuccess ? <Checkmark16Regular className={styles.checkMark} /> : <Copy16Regular />}
                appearance="transparent"
                onClick={() => {
                    void handleCopyToClipboard();
                }}
                aria-label="copy"
            />
        </Tooltip>
    );
};

export default CopyChatButton;
